'use client';

import { format } from 'date-fns';
import { Calendar as CalendarIcon } from 'lucide-react';
import { useState } from 'react';
import type * as React from 'react';
import type { DateRange } from 'react-day-picker';

import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface DateRangePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  dateRange: DateRange | undefined;
  setDateRange: (dateRange: DateRange | undefined) => void;
  className?: string;
}

/**
 * DateRangePicker component for selecting date ranges.
 *
 * **IMPORTANT**: When using with API calls, always pass the selected dates
 * through `prepareDateRangeForQuery()` from `@/utils/date-range` to ensure
 * consistent date range handling across the application.
 *
 * This component returns raw Date objects - DO NOT manually adjust dates
 * (e.g., with `.setHours()`) before API calls. The shared utility handles
 * all date normalization and ensures correct inclusive/exclusive semantics.
 *
 * @example Correct usage with API calls
 * ```tsx
 * import { prepareDateRangeForQuery } from '@/utils/date-range';
 *
 * const [dateRange, setDateRange] = useState<DateRange | undefined>();
 * const { startDate, endDate } = prepareDateRangeForQuery(dateRange?.from, dateRange?.to);
 *
 * const { data } = trpc.calls.getSummary.useQuery({ startDate, endDate });
 * ```
 *
 * @example Incorrect usage (DO NOT DO THIS)
 * ```tsx
 * // ❌ WRONG - Manual date adjustment causes double adjustment bug
 * const endDate = dateRange?.to
 *   ? new Date(dateRange.to.setHours(23, 59, 59, 999)).toISOString()
 *   : undefined;
 * ```
 */
export function DateRangePicker({ dateRange, setDateRange, className }: DateRangePickerProps) {
  return (
    <div className={cn('grid gap-2', className)}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={'outline'}
            className={cn(
              'w-[300px] justify-start text-left font-normal',
              !dateRange && 'text-muted-foreground'
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {dateRange?.from ? (
              dateRange.to ? (
                <>
                  {format(dateRange.from, 'LLL dd, y')} - {format(dateRange.to, 'LLL dd, y')}
                </>
              ) : (
                format(dateRange.from, 'LLL dd, y')
              )
            ) : (
              <span>Pick a date range</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar
            initialFocus
            mode="range"
            defaultMonth={dateRange?.from}
            selected={dateRange}
            onSelect={setDateRange}
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

export interface DatePickerProps {
  date: Date | undefined;
  onDateChange: (date: Date | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
  disablePast?: boolean;
  disableFuture?: boolean;
  className?: string;
  align?: 'start' | 'center' | 'end';
  formatStr?: string;
  buttonVariant?: 'default' | 'outline' | 'ghost' | 'secondary';
}

export function DatePicker({
  date,
  onDateChange,
  placeholder = 'Pick a date',
  disabled = false,
  disablePast = false,
  disableFuture = false,
  className,
  align = 'start',
  formatStr = 'PPP',
  buttonVariant = 'outline',
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState<Date>(date || new Date());

  const handleSelect = (newDate: Date | undefined) => {
    onDateChange(newDate);
    setOpen(false);
  };

  const isDateDisabled = (checkDate: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const compareDate = new Date(checkDate);
    compareDate.setHours(0, 0, 0, 0);

    if (disablePast && compareDate < today) return true;
    if (disableFuture && compareDate > today) return true;
    return false;
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant={buttonVariant}
          className={cn(
            'justify-start text-left font-normal',
            !date && 'text-muted-foreground',
            className
          )}
          disabled={disabled}
          type="button"
        >
          <CalendarIcon className="mr-2 h-4 w-4" />
          {date ? format(date, formatStr) : <span>{placeholder}</span>}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align={align}>
        <Calendar
          mode="single"
          selected={date}
          onSelect={handleSelect}
          month={month}
          onMonthChange={setMonth}
          initialFocus
          disabled={isDateDisabled}
        />
      </PopoverContent>
    </Popover>
  );
}
